import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Layers, 
  X, 
  Save, 
  Box,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: '', taxRate: '0' });

  const loadCategories = () => {
    api.get('/products/categories').then(res => setCategories(res.data)).catch(console.error);
  };

  useEffect(() => { loadCategories(); }, []);

  const openModal = (category = null) => {
    setCurrentCategory(category);
    if (category) {
      setFormData({ name: category.name, icon: category.icon || '', taxRate: category.taxRate !== null ? category.taxRate.toString() : '' });
    } else {
      setFormData({ name: '', icon: '', taxRate: '' });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCategory(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (currentCategory) await api.put(`/products/categories/${currentCategory.id}`, formData);
      else await api.post('/products/categories', formData);
      closeModal();
      loadCategories();
    } catch (err) {
      alert('Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Products in this category will be unassigned.')) return;
    try {
      await api.delete(`/products/categories/${id}`);
      loadCategories();
    } catch (err) {
      alert('Error deleting category. It might be in use.');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} categories?`)) return;
    try {
      await api.post('/products/categories/bulk-delete', { ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      loadCategories();
    } catch (err) {
      alert('Error deleting categories');
    }
  };

  const filteredCategories = categories.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // Pagination Logic
  const totalItems = filteredCategories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentCategories.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentCategories.map(c => c.id)));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-5xl mx-auto pb-10">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Categories</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Organize your product catalog into logical groups.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button onClick={() => openModal()} className="bg-brand hover:bg-brand-light text-white px-4 py-2 rounded-xl shadow-md font-bold text-sm flex items-center gap-2 transition-all">
            <Plus size={18} /> Add Category
          </button>
        </div>
      </div>

      <div className="bg-theme-card rounded-[2rem] shadow-soft border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
          <div className="relative w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-theme-muted" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 sm:text-sm rounded-xl py-2.5 bg-theme-main border border-theme text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="flex items-center gap-2 text-[10px] font-black text-theme-muted uppercase tracking-widest">
            <Layers size={16} /> {filteredCategories.length} total
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                {isAdmin && (
                  <th className="pl-6 py-4 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentCategories.length && currentCategories.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>Category Name</th>
                <th className="px-6 py-4 text-center">Products</th>
                <th className="px-6 py-4 text-center">Tax Rate</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentCategories.map(c => (
                <tr key={c.id} className={`hover:bg-theme-main/10 transition-colors group ${selectedIds.has(c.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-6 py-4">
                      <button onClick={() => toggleSelect(c.id)} className={selectedIds.has(c.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(c.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center text-brand font-black border border-brand/20 shadow-sm text-lg">
                        {c.icon ? c.icon : c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="font-bold text-theme-main">{c.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-theme-main text-theme-muted border border-theme">
                      <Box size={14} className="opacity-50" />
                      {c._count?.products || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {c.taxRate !== null ? (
                      <span className="text-xs font-black text-theme-main">{c.taxRate}%</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-brand/10 text-brand text-[9px] font-black uppercase tracking-widest border border-brand/20">Global</span>
                    )}
                  </td>
                  <td className="px-8 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openModal(c)} className="p-1.5 text-theme-muted hover:text-brand hover:bg-theme-main rounded-lg transition-all">
                        <Edit2 size={16} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(c.id)} className="p-1.5 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentCategories.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? "5" : "4"} className="px-6 py-12 text-center text-theme-muted font-bold italic uppercase tracking-widest text-[10px] opacity-50">
                    No categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> categories
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronLeft size={20} /></button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronRight size={20} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Category Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col border border-theme">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">{currentCategory ? 'Edit Category' : 'Add New Category'}</h3>
              <button onClick={closeModal} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Category Name</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-bold focus:ring-2 focus:ring-brand outline-none transition-all" placeholder="e.g. Beverages" />
              </div>
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Icon / Emoji</label>
                <input type="text" maxLength="2" value={formData.icon} onChange={e => setFormData({...formData, icon: e.target.value})} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all text-xl" placeholder="🍔" />
              </div>
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Tax Rate (%)</label>
                <div className="relative">
                  <input 
                    type="number" 
                    step="0.01" 
                    value={formData.taxRate} 
                    onChange={e => setFormData({...formData, taxRate: e.target.value})} 
                    className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-black" 
                    placeholder="Inherit Store Tax" 
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-theme-muted font-bold">%</span>
                </div>
                <p className="text-[10px] text-theme-muted mt-1 uppercase font-bold tracking-tight italic">Leave blank to use global store tax.</p>
              </div>

              <div className="pt-6 border-t border-theme flex gap-3 mt-6">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-bold hover:bg-theme-main transition-all">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-3 bg-brand hover:bg-brand-light text-white rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2">
                  <Save size={18} /> Save Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
